import { ILookupModel, IMultiSelectOption } from '../../../../interfaces/ILookup';
import { Stack, Table } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import CheckmateNSelect from '../../../shared/CheckmateNSelect';
import { ISameCaseMatchedCasesSearchModel } from '../../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import { LoadingButton } from '../../../../shared/buttons/LoadingButton';
import { useGetJurisdictions } from '../../../../shared/react-query-hooks/useGetJurisdictions';
import { useGetUsStatesLookup } from '../../../../shared/react-query-hooks/useUsStatesLookup';

const lookupToOptionMapper = (lookupItem: ILookupModel) =>
    ({
        label: lookupItem.displayName!,
        value: lookupItem.name!,
        id: lookupItem.id,
        guid: lookupItem.guid,
    }) as IMultiSelectOption;

const lookupToStateOptionMapper = (lookupItem: ILookupModel) =>
    ({
        id: lookupItem.id,
        label: `${lookupItem.displayName} - ${lookupItem.name}`,
        value: lookupItem.name ?? '',
        guid: lookupItem.guid ?? '',
    }) as IMultiSelectOption;

export interface ICaseGroupsSearchFiltersProps {
    currentFilters: ISameCaseMatchedCasesSearchModel;
    isSearching?: boolean;
    onApplyFilters: (filters: ISameCaseMatchedCasesSearchModel) => void;
}

export function SameCasesAdminSearchFilters(props: ICaseGroupsSearchFiltersProps) {
    const [localFilters, setLocalFilters] = useState<ISameCaseMatchedCasesSearchModel>(
        props.currentFilters
    );

    const jurisdictionsLookup = useGetJurisdictions(props.currentFilters.caseFileModelId);
    const usStatesLookup = useGetUsStatesLookup();

    const availableStateOptions = usStatesLookup.data?.map(
        lookupToStateOptionMapper
    ) as IMultiSelectOption[];

    const selectedStateOptions =
        localFilters.states
            ?.map((stateId) => availableStateOptions.find((lu) => lu.guid === stateId))
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const availableJurisdictionOptions =
        jurisdictionsLookup.data
            ?.filter((x) => {
                if (selectedStateOptions?.length === 0) return true;
                return selectedStateOptions
                    .flatMap((state) => state.value)
                    .includes(x.displayName?.slice(-2) ?? '');
            })
            .map(lookupToOptionMapper) ?? [];

    const selectedJurisdictionOptions =
        localFilters.jurisdictions
            ?.map((jurisdictionId) =>
                availableJurisdictionOptions.find((lu) => lu.guid === jurisdictionId)
            )
            .filter((val): val is IMultiSelectOption => val !== undefined) ?? [];

    const handleApplyFiltersClick = () => {
        props.onApplyFilters(localFilters);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleApplyFiltersClick();
        }
    };

    const handleCaseNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, caseNumber: e.target.value }));
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalFilters((prev) => ({ ...prev, name: e.target.value }));
    };

    const handleJurisdictionsChange = (selectedItems: IMultiSelectOption[]) => {
        const jurisdictionGuids = selectedItems?.map((x) => x.guid!);
        setLocalFilters((prev) => ({ ...prev, jurisdictions: jurisdictionGuids }));
    };

    const handleStatesChange = (selectedItems: IMultiSelectOption[]) => {
        const stateGuids = selectedItems?.map((x) => x.guid!);
        const foundStateLookupItems = selectedItems.map((item) =>
            (usStatesLookup.data ?? []).find((lu) => lu.guid === item.guid)
        );
        const foundStateAbbreviations = foundStateLookupItems.map((item) => item?.name);

        // for each state, if a jurisdiction exists that doesn't belong to a selected state, remove i
        const filteredJurisdictions = localFilters.jurisdictions?.filter((j) => {
            const found = jurisdictionsLookup.data?.find((lu) => lu.guid === j);
            if (found) {
                return foundStateAbbreviations.includes(found.name?.slice(-2));
            }

            return false;
        });

        setLocalFilters((prev) => ({
            ...prev,
            states: stateGuids,
            jurisdictions: filteredJurisdictions,
        }));
    };

    const handleClearFiltersClick = () => {
        setLocalFilters({
            ...localFilters,
            pagedModel: { ...localFilters.pagedModel, pageNumber: 1 },
            caseNumber: undefined,
            jurisdictions: undefined,
            name: undefined,
            states: undefined,
        } as ISameCaseMatchedCasesSearchModel);
    };

    useEffect(() => {
        setLocalFilters(props.currentFilters);
    }, [props.currentFilters]);

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">State</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleStatesChange}
                                    options={availableStateOptions}
                                    value={selectedStateOptions}
                                    disabled={usStatesLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Jurisdiction</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleJurisdictionsChange}
                                    options={availableJurisdictionOptions}
                                    value={selectedJurisdictionOptions}
                                    disabled={jurisdictionsLookup.isLoading}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Case Number</td>
                            <td>
                                <input
                                    className="form-control full-width"
                                    type="text"
                                    value={localFilters.caseNumber ?? ''}
                                    onChange={handleCaseNumberChange}
                                />
                            </td>
                        </tr>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Case Name</td>
                            <td>
                                <input
                                    className="form-control full-width"
                                    type="text"
                                    value={localFilters.name ?? ''}
                                    onKeyDown={handleKeyDown}
                                    onChange={handleNameChange}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2}>
                                <hr />
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />

                <button
                    className="btn btn-default"
                    onClick={handleClearFiltersClick}
                    disabled={props.isSearching}
                >
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
}
